// Halaman kontribusi anime (/web/kontribusi): pengunjung mengajukan anime
// untuk ditayangkan di DisboxAnime, lewat link (Google Drive dll) atau upload
// file langsung. Kiriman masuk antrean tinjauan admin (/web/kontribusi-admin,
// lihat discord-bot/contrib.js). Kode kiriman disimpan di browser pengirim
// supaya statusnya bisa dicek lagi.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AppBar, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, CssBaseline, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment, LinearProgress, MenuItem, Tab, Tabs,
    TextField, Toolbar, Typography, useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MovieIcon from '@mui/icons-material/Movie';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { searchAnime, FORMAT_LABELS } from './anilist';
import { publicApiBase } from './api.js';
import { formatSize } from './file-utils';

const ACCENT = '#ff7a1a';
const MAX_FILE_BYTES = 8 * 1024 ** 3;
const MAX_TOTAL_BYTES = 30 * 1024 ** 3;
const MAX_LINKS = 10;
const VIDEO_TYPES = /\.(mkv|mp4|webm)$/i;
const SUB_TYPES = /\.(ass|ssa|srt|vtt)$/i;
const ARCHIVE_TYPES = /\.(rar|zip|7z)$/i;
const MINE_KEY = 'disbox_kontribusi_saya';

function readMine() {
    try {
        const list = JSON.parse(localStorage.getItem(MINE_KEY));
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
}

function rememberMine(entry) {
    try {
        localStorage.setItem(MINE_KEY, JSON.stringify([entry, ...readMine().filter((e) => e.code !== entry.code)].slice(0, 20)));
    } catch {
        // localStorage penuh / diblokir: kode tetap ditampilkan di layar.
    }
}

async function api(pathWithQuery, options = {}) {
    const response = await fetch(`${publicApiBase()}${pathWithQuery}`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Server membalas status ${response.status}`);
    return data;
}

function putFile(url, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = (e) => onProgress(e.loaded);
        xhr.onload = () => {
            if (xhr.status < 300) resolve();
            else {
                let message = `status ${xhr.status}`;
                try {
                    message = JSON.parse(xhr.responseText).error || message;
                } catch { /* bukan JSON */ }
                reject(new Error(message));
            }
        };
        xhr.onerror = () => reject(new Error('Koneksi terputus.'));
        xhr.send(file);
    });
}

function isAccepted(name) {
    return VIDEO_TYPES.test(name) || SUB_TYPES.test(name) || ARCHIVE_TYPES.test(name);
}

const theme = createTheme({
    shape: { borderRadius: 10 },
    palette: {
        mode: 'dark',
        primary: { main: ACCENT, contrastText: '#000' },
        background: { default: '#0e0e12', paper: '#18181f' },
    },
    typography: { button: { textTransform: 'none', fontWeight: 700 } },
    components: {
        MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
        MuiButton: { styleOverrides: { root: { borderRadius: 10 } } },
    },
});

const SUB_LANGS = ['Indonesia', 'English', 'Indonesia + English', 'Tanpa subtitle', 'Lainnya'];
const QUALITIES = ['1080p', '720p', '480p', 'Lainnya'];
const SOURCES = ['BD (Blu-ray)', 'WEB', 'TV', 'Tidak tahu'];

function Card({ children, sx }) {
    return <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,.06)', p: { xs: 2, md: 3 }, ...sx }}>{children}</Box>;
}

function SectionTitle({ number, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: ACCENT, color: '#000', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{number}</Box>
            <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.3 }}>{title}</Typography>
                {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
            </Box>
        </Box>
    );
}

function Step({ icon, title, text }) {
    return (
        <Box sx={{ flex: 1, minWidth: 200, display: 'flex', gap: 1.5, p: 2, borderRadius: 3, bgcolor: 'rgba(255,122,26,.08)', border: '1px solid rgba(255,122,26,.25)' }}>
            <Box sx={{ color: ACCENT, mt: 0.25 }}>{icon}</Box>
            <Box>
                <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
                <Typography variant="body2" color="text.secondary">{text}</Typography>
            </Box>
        </Box>
    );
}

// Pencarian judul ke AniList, hasil dengan poster.
function AnimeSearch({ value, onChange }) {
    const [input, setInput] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (input.trim().length < 2) {
            setOptions([]);
            return undefined;
        }
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                setOptions(await searchAnime(input, controller.signal));
            } catch {
                // dibatalkan / offline
            } finally {
                setLoading(false);
            }
        }, 400);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [input]);

    return (
        <Autocomplete
            value={value}
            onChange={(e, next) => onChange(next)}
            inputValue={input}
            onInputChange={(e, next) => setInput(next)}
            options={value && !options.some((o) => o.id === value.id) ? [value, ...options] : options}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o.title || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={loading}
            noOptionsText={input.trim().length < 2 ? 'Ketik minimal 2 huruf' : 'Tidak ketemu, coba judul lain'}
            loadingText="Mencari di AniList..."
            renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Box sx={{ width: 36, height: 52, borderRadius: 1, flexShrink: 0, bgcolor: '#23232c', backgroundImage: o.cover ? `url("${o.cover}")` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{o.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {[o.year, FORMAT_LABELS[o.format], o.episodes && `${o.episodes} episode`].filter(Boolean).join(' · ')}
                        </Typography>
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Judul anime"
                    placeholder="Contoh: Sousou no Frieren"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: <>{loading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>,
                    }}
                />
            )}
        />
    );
}

function SelectedAnime({ anime }) {
    if (!anime) return null;
    return (
        <Box sx={{ display: 'flex', gap: 2, mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,.04)', alignItems: 'center' }}>
            <Box sx={{ width: 64, aspectRatio: '2 / 3', borderRadius: 1.5, flexShrink: 0, bgcolor: '#23232c', backgroundImage: anime.cover ? `url("${anime.cover}")` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>{anime.title}</Typography>
                {anime.titleEnglish && anime.titleEnglish !== anime.title && <Typography variant="body2" color="text.secondary">{anime.titleEnglish}</Typography>}
                <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                    {anime.year && <Chip size="small" label={anime.year} />}
                    {FORMAT_LABELS[anime.format] && <Chip size="small" label={FORMAT_LABELS[anime.format]} />}
                    {anime.episodes && <Chip size="small" label={`${anime.episodes} episode`} />}
                </Box>
            </Box>
        </Box>
    );
}

function DropZone({ files, onAdd, onRemove, maxFile }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const total = files.reduce((sum, f) => sum + f.size, 0);
    const pick = (list) => onAdd(Array.from(list || []));
    return (
        <Box>
            <Box
                onClick={() => inputRef.current && inputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}
                sx={{
                    cursor: 'pointer', textAlign: 'center', p: { xs: 3, md: 4 }, borderRadius: 3,
                    border: `2px dashed ${dragging ? ACCENT : 'rgba(255,255,255,.18)'}`,
                    bgcolor: dragging ? 'rgba(255,122,26,.08)' : 'rgba(255,255,255,.02)',
                    transition: 'all .15s',
                    '&:hover': { borderColor: ACCENT },
                }}
            >
                <CloudUploadIcon sx={{ fontSize: 44, color: ACCENT }} />
                <Typography sx={{ fontWeight: 700, mt: 1 }}>Seret file ke sini, atau klik untuk memilih</Typography>
                <Typography variant="body2" color="text.secondary">Video MKV / MP4 / WebM, arsip RAR / ZIP / 7z berisi episode, dan subtitle ASS / SRT. {maxFile ? `Maksimal ${formatSize(maxFile)} per file.` : 'Tanpa batas ukuran (jaringan kantor).'}</Typography>
                <input ref={inputRef} type="file" multiple hidden accept=".mkv,.mp4,.webm,.ass,.ssa,.srt,.vtt,.rar,.zip,.7z" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
            </Box>
            {files.length > 0 && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {files.map((f, i) => {
                        const problem = !isAccepted(f.name) ? 'Jenis file tidak didukung'
                            : maxFile && f.size > maxFile ? 'Terlalu besar' : null;
                        return (
                            <Box key={`${f.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,.04)', border: problem ? '1px solid #f44336' : '1px solid transparent' }}>
                                {SUB_TYPES.test(f.name) ? <SubtitlesIcon color="action" /> : ARCHIVE_TYPES.test(f.name) ? <FolderZipIcon sx={{ color: ACCENT }} /> : <MovieIcon sx={{ color: ACCENT }} />}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={f.name}>{f.name}</Typography>
                                    <Typography variant="caption" color={problem ? 'error' : 'text.secondary'}>{problem || formatSize(f.size)}</Typography>
                                </Box>
                                <IconButton size="small" onClick={() => onRemove(i)} aria-label="Hapus"><DeleteOutlineIcon fontSize="small" /></IconButton>
                            </Box>
                        );
                    })}
                    <Typography variant="caption" color="text.secondary">{files.length} file · total {formatSize(total)}</Typography>
                </Box>
            )}
        </Box>
    );
}

function Guide() {
    const item = (title, text) => (
        <Box sx={{ mb: 1.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{text}</Typography>
        </Box>
    );
    return (
        <Card>
            <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Panduan singkat</Typography>
            {item('Format video', 'MKV atau MP4. Subtitle tertanam (ASS) didukung lengkap dengan font dan efeknya.')}
            {item('Batch RAR / ZIP', 'Satu arsip berisi semua episode boleh. Kalau dikunci, isi password-nya. RAR terpecah (part1, part2, ...) kirim semua bagiannya.')}
            {item('Nama file tidak masalah', 'Judul diambil dari anime yang kamu pilih, file dinamai ulang otomatis. Nomor episode dibaca dari nama file, kalau tidak ada dipakai urutan file.')}
            {item('Satu kiriman, satu judul', 'Boleh banyak episode sekaligus, asal dari anime dan season yang sama.')}
            {item('Link Google Drive', 'Pastikan file dibagikan ke "Siapa saja yang memiliki link". Kirim link per file (bukan link folder).')}
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Tidak diterima</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                    <li>Konten dewasa atau yang melanggar hukum</li>
                    <li>File rusak, terpotong, atau berisi iklan tempelan</li>
                    <li>Anime yang sudah lengkap tersedia di sini</li>
                </Box>
            </Typography>
        </Card>
    );
}

const STATUS_COLORS = { 'Sudah tayang': 'success', Ditolak: 'error', 'Sedang diunggah': 'warning' };

// Kiriman yang pernah dikirim dari browser ini (kode + token), dengan status terbaru.
function MyContributions({ refreshKey }) {
    const [items, setItems] = useState(() => readMine());
    const [statuses, setStatuses] = useState({});
    useEffect(() => {
        const list = readMine();
        setItems(list);
        let cancelled = false;
        list.forEach((entry) => {
            api(`/kontribusi/${encodeURIComponent(entry.code)}?token=${encodeURIComponent(entry.token)}`)
                .then((data) => { if (!cancelled) setStatuses((prev) => ({ ...prev, [entry.code]: data })); })
                .catch(() => { if (!cancelled) setStatuses((prev) => ({ ...prev, [entry.code]: { label: 'Tidak ditemukan' } })); });
        });
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);
    if (items.length === 0) return null;
    return (
        <Card>
            <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Kiriman kamu</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {items.map((entry) => {
                    const status = statuses[entry.code];
                    return (
                        <Box key={entry.code} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <Box sx={{ width: 36, height: 52, borderRadius: 1, flexShrink: 0, bgcolor: '#23232c', backgroundImage: entry.cover ? `url("${entry.cover}")` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{entry.title}</Typography>
                                <Typography variant="caption" color="text.secondary">Kode {entry.code} · {new Date(entry.createdAt).toLocaleDateString('id-ID')}</Typography>
                                {status && status.reason && <Typography variant="caption" color="error" sx={{ display: 'block' }}>Alasan: {status.reason}</Typography>}
                            </Box>
                            <Chip size="small" label={status ? status.label : 'Memuat...'} color={(status && STATUS_COLORS[status.label]) || 'default'} variant={status && STATUS_COLORS[status.label] ? 'filled' : 'outlined'} />
                        </Box>
                    );
                })}
            </Box>
        </Card>
    );
}

export default function ContributePage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [anime, setAnime] = useState(null);
    const [season, setSeason] = useState('');
    const [episodes, setEpisodes] = useState('');
    const [subLang, setSubLang] = useState('Indonesia');
    const [quality, setQuality] = useState('1080p');
    const [source, setSource] = useState('BD (Blu-ray)');
    const [method, setMethod] = useState('link');
    const [files, setFiles] = useState([]);
    const [links, setLinks] = useState(['']);
    const [archivePassword, setArchivePassword] = useState('');
    const [name, setName] = useState('');
    const [contact, setContact] = useState('');
    const [note, setNote] = useState('');
    const [agreeRights, setAgreeRights] = useState(false);
    const [agreeReview, setAgreeReview] = useState(false);
    const [sending, setSending] = useState(null); // { message, loaded, total }
    const [error, setError] = useState(null);
    const [done, setDone] = useState(null); // { code, token, title }
    const [refreshKey, setRefreshKey] = useState(0);
    const [copied, setCopied] = useState(false);
    // Batas dari bot: pengunjung publik dibatasi, jaringan kantor tidak.
    const [limits, setLimits] = useState({ trusted: false, maxFileBytes: MAX_FILE_BYTES, maxTotalBytes: MAX_TOTAL_BYTES, maxLinks: MAX_LINKS });

    useEffect(() => {
        api('/kontribusi-batas').then((data) => setLimits((prev) => ({ ...prev, ...data }))).catch(() => {});
    }, []);

    useEffect(() => {
        document.title = 'Kontribusi Anime | DisboxAnime';
        return () => {
            document.title = 'Disbox';
        };
    }, []);

    // Link status dari kiriman lain (?kode=...&token=...) disimpan ke daftar.
    useEffect(() => {
        const code = params.get('kode');
        const token = params.get('token');
        if (!code || !token) return;
        api(`/kontribusi/${encodeURIComponent(code)}?token=${encodeURIComponent(token)}`)
            .then((data) => {
                rememberMine({ code: data.code, token, title: data.anime.title, cover: data.anime.cover, createdAt: data.createdAt });
                setRefreshKey((k) => k + 1);
            })
            .catch(() => {});
    }, [params]);

    const validFiles = files.filter((f) => isAccepted(f.name) && (!limits.maxFileBytes || f.size <= limits.maxFileBytes));
    const mainCount = validFiles.filter((f) => VIDEO_TYPES.test(f.name) || ARCHIVE_TYPES.test(f.name)).length;
    const totalBytes = validFiles.reduce((sum, f) => sum + f.size, 0);
    const cleanLinks = links.map((l) => l.trim()).filter(Boolean);
    const badLinks = cleanLinks.filter((l) => !/^https:\/\/\S+\.\S+/i.test(l));
    const missing = useMemo(() => [
        !anime && 'judul anime',
        method === 'file' && mainCount === 0 && 'minimal satu file video atau arsip',
        method === 'file' && validFiles.length !== files.length && 'hapus file yang ditandai merah',
        method === 'file' && limits.maxTotalBytes && totalBytes > limits.maxTotalBytes && `total file maksimal ${formatSize(limits.maxTotalBytes)}`,
        method === 'link' && (cleanLinks.length === 0 || badLinks.length > 0) && 'link yang valid (diawali https://)',
        !name.trim() && 'nama kamu',
        !agreeRights && 'persetujuan hak berbagi',
        !agreeReview && 'persetujuan peninjauan',
    ].filter(Boolean), [anime, method, mainCount, validFiles.length, files.length, totalBytes, limits.maxTotalBytes, cleanLinks.length, badLinks.length, name, agreeRights, agreeReview]);
    const progress = Math.round(((8 - missing.length) / 8) * 100);

    const reset = () => {
        setAnime(null);
        setSeason('');
        setEpisodes('');
        setFiles([]);
        setLinks(['']);
        setArchivePassword('');
        setNote('');
        setAgreeRights(false);
        setAgreeReview(false);
    };

    const submit = useCallback(async () => {
        setError(null);
        setSending({ message: 'Mengirim data kiriman...', loaded: 0, total: 0 });
        try {
            const body = {
                anime: { id: anime.id },
                season, episodes, subLang, quality, source, method,
                links: method === 'link' ? cleanLinks : [],
                files: method === 'file' ? validFiles.map((f) => ({ name: f.name, size: f.size })) : [],
                archivePassword, name, contact, note, agreeRights, agreeReview,
            };
            const created = await api('/kontribusi', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            const entry = { code: created.code, token: created.token, title: anime.title, cover: anime.cover, createdAt: Date.now() };
            rememberMine(entry);
            if (method === 'file') {
                let before = 0;
                for (const [index, file] of validFiles.entries()) {
                    const url = `${publicApiBase()}/kontribusi/${created.code}/file/${index}?token=${encodeURIComponent(created.token)}`;
                    for (let attempt = 1; ; attempt++) {
                        setSending({ message: `Mengunggah ${file.name} (${index + 1}/${validFiles.length})${attempt > 1 ? `, percobaan ${attempt}` : ''}`, loaded: before, total: totalBytes });
                        try {
                            // eslint-disable-next-line no-loop-func
                            await putFile(url, file, (loaded) => setSending((s) => ({ ...s, loaded: before + loaded })));
                            break;
                        } catch (uploadError) {
                            if (attempt >= 3) throw uploadError;
                            await new Promise((r) => setTimeout(r, 3000 * attempt));
                        }
                    }
                    before += file.size;
                }
                await api(`/kontribusi/${created.code}/kirim?token=${encodeURIComponent(created.token)}`, { method: 'POST' });
            }
            setDone(entry);
            reset();
            setRefreshKey((k) => k + 1);
        } catch (sendError) {
            setError(sendError.message);
            setRefreshKey((k) => k + 1);
        } finally {
            setSending(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anime, season, episodes, subLang, quality, source, method, links, files, archivePassword, name, contact, note, agreeRights, agreeReview]);

    const statusLink = done ? `${window.location.origin}${window.location.pathname}?kode=${done.code}&token=${encodeURIComponent(done.token)}` : '';

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
                <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(14,14,18,.95)', backdropFilter: 'blur(8px)' }}>
                    <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 }, px: { xs: 1, md: 3 } }}>
                        <IconButton color="inherit" onClick={() => navigate('/nonton')} aria-label="Kembali"><ArrowBackIcon /></IconButton>
                        <Box onClick={() => navigate('/nonton')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                            <LiveTvIcon sx={{ color: ACCENT }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: 0.3 }}>
                                Disbox<Box component="span" sx={{ color: ACCENT }}>Anime</Box>
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        {isDesktop && <Button color="inherit" onClick={() => navigate('/nonton')}>Kembali menonton</Button>}
                    </Toolbar>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 3, bgcolor: 'transparent' }} />
                </AppBar>

                <Box sx={{ position: 'relative', overflow: 'hidden', background: 'radial-gradient(circle at 15% 0%, rgba(255,122,26,.25), transparent 55%), radial-gradient(circle at 90% 20%, rgba(120,90,255,.18), transparent 50%)' }}>
                    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, pt: { xs: 4, md: 7 }, pb: { xs: 3, md: 5 } }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip icon={<VolunteerActivismIcon />} label="Kontribusi komunitas" sx={{ bgcolor: 'rgba(255,122,26,.15)', color: ACCENT, fontWeight: 700, '& .MuiChip-icon': { color: ACCENT } }} />
                            {limits.trusted && <Chip color="success" variant="outlined" label="Jaringan kantor: tanpa batas" />}
                        </Box>
                        <Typography sx={{ fontWeight: 900, fontSize: { xs: 30, md: 46 }, lineHeight: 1.1, mt: 1.5 }}>
                            Bagikan anime favoritmu<br />ke <Box component="span" sx={{ color: ACCENT }}>DisboxAnime</Box>
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 640 }}>
                            Punya koleksi anime dengan kualitas bagus dan subtitle lengkap? Kirim ke sini. Setiap kiriman ditinjau
                            admin dulu, lalu tayang untuk semua penonton dengan namamu sebagai kontributor.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                            <Step icon={<SearchIcon />} title="1. Pilih anime" text="Cari judulnya, poster & info diambil otomatis." />
                            <Step icon={<CloudUploadIcon />} title="2. Kirim file" text="Tempel link Google Drive atau upload langsung." />
                            <Step icon={<RateReviewIcon />} title="3. Ditinjau & tayang" text="Admin memeriksa isinya, lalu menayangkannya." />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, pb: 8, display: 'flex', gap: 3, flexDirection: isDesktop ? 'row' : 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Card>
                            <SectionTitle number="1" title="Anime apa yang mau kamu bagikan?" subtitle="Judul ini yang dipakai, apa pun nama file atau foldernya." />
                            <AnimeSearch value={anime} onChange={setAnime} />
                            <SelectedAnime anime={anime} />
                            <Box sx={{ display: 'grid', gap: 2, mt: 2.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                <TextField label="Season / bagian (opsional)" placeholder="Contoh: Season 2" value={season} onChange={(e) => setSeason(e.target.value)} inputProps={{ maxLength: 60 }} />
                                <TextField label="Episode" placeholder="Contoh: 1-12, atau Film" value={episodes} onChange={(e) => setEpisodes(e.target.value)} inputProps={{ maxLength: 40 }} />
                                <TextField select label="Subtitle" value={subLang} onChange={(e) => setSubLang(e.target.value)}>
                                    {SUB_LANGS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField select fullWidth label="Kualitas" value={quality} onChange={(e) => setQuality(e.target.value)}>
                                        {QUALITIES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                    </TextField>
                                    <TextField select fullWidth label="Sumber" value={source} onChange={(e) => setSource(e.target.value)}>
                                        {SOURCES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                    </TextField>
                                </Box>
                            </Box>
                        </Card>

                        <Card>
                            <SectionTitle number="2" title="Kirim filenya" subtitle="Pilih salah satu cara." />
                            <Tabs value={method} onChange={(e, v) => setMethod(v)} sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}>
                                <Tab value="link" icon={<InsertLinkIcon fontSize="small" />} iconPosition="start" label="Kirim link" />
                                <Tab value="file" icon={<CloudUploadIcon fontSize="small" />} iconPosition="start" label="Upload file" />
                            </Tabs>
                            {method === 'file' ? (
                                <DropZone files={files} maxFile={limits.maxFileBytes} onAdd={(list) => setFiles((prev) => [...prev, ...list])} onRemove={(i) => setFiles((prev) => prev.filter((_, j) => j !== i))} />
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {links.map((value, i) => {
                                        const bad = value.trim() && !/^https:\/\/\S+\.\S+/i.test(value.trim());
                                        return (
                                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <TextField fullWidth label={links.length > 1 ? `Link ${i + 1}` : 'Link file'} placeholder="https://drive.google.com/file/d/..." value={value}
                                                    onChange={(e) => setLinks((prev) => prev.map((l, j) => (j === i ? e.target.value : l)))}
                                                    error={Boolean(bad)} helperText={bad ? 'Link harus diawali https://' : null}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><InsertLinkIcon fontSize="small" /></InputAdornment> }} />
                                                {links.length > 1 && <IconButton onClick={() => setLinks((prev) => prev.filter((_, j) => j !== i))} sx={{ mt: 1 }} aria-label="Hapus link"><DeleteOutlineIcon /></IconButton>}
                                            </Box>
                                        );
                                    })}
                                    <Box>
                                        <Button startIcon={<AddIcon />} disabled={links.length >= (limits.maxLinks || MAX_LINKS)} onClick={() => setLinks((prev) => [...prev, ''])}>Tambah link</Button>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Google Drive (dibagikan ke "Siapa saja yang memiliki link") atau link unduhan langsung. Untuk RAR terpecah, masukkan link tiap bagian.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <TextField fullWidth label="Password arsip (kalau ada)" placeholder="Kosongkan kalau RAR/ZIP tidak dikunci" value={archivePassword} onChange={(e) => setArchivePassword(e.target.value)} sx={{ mt: 2.5 }}
                                inputProps={{ maxLength: 100, autoComplete: 'off' }} InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment> }} />
                        </Card>

                        <Card>
                            <SectionTitle number="3" title="Tentang kamu" subtitle="Namamu tampil sebagai kontributor. Kontak hanya dilihat admin." />
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                <TextField label="Nama / nama panggilan" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 40 }} />
                                <TextField label="Kontak (opsional)" placeholder="Username Discord atau email" value={contact} onChange={(e) => setContact(e.target.value)} inputProps={{ maxLength: 100 }} />
                            </Box>
                            <TextField fullWidth multiline minRows={3} label="Catatan untuk admin (opsional)" placeholder="Misalnya: episode 5 subtitle-nya terpisah, versi uncensored, dll." value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }} inputProps={{ maxLength: 500 }} />
                            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel control={<Checkbox checked={agreeRights} onChange={(e) => setAgreeRights(e.target.checked)} />}
                                    label={<Typography variant="body2">File ini boleh saya bagikan dan tidak berisi konten terlarang.</Typography>} />
                                <FormControlLabel control={<Checkbox checked={agreeReview} onChange={(e) => setAgreeReview(e.target.checked)} />}
                                    label={<Typography variant="body2">Saya mengerti kiriman ditinjau dulu dan bisa ditolak tanpa alasan.</Typography>} />
                            </Box>
                        </Card>

                        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Button variant="contained" size="large" startIcon={<SendIcon />} disabled={missing.length > 0 || Boolean(sending)} onClick={submit} sx={{ px: 4 }}>
                                Kirim kontribusi
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                                {missing.length > 0 ? `Belum lengkap: ${missing.join(', ')}.` : method === 'file' ? `Siap mengunggah ${validFiles.length} file (${formatSize(totalBytes)}).` : 'Semua sudah lengkap.'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ width: isDesktop ? 340 : '100%', flexShrink: 0, position: isDesktop ? 'sticky' : 'static', top: 88, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <MyContributions refreshKey={refreshKey} />
                        <Guide />
                    </Box>
                </Box>
            </Box>

            <Dialog open={Boolean(sending)} fullWidth maxWidth="xs">
                <DialogTitle>Mengirim kontribusi</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5, wordBreak: 'break-word' }}>{sending && sending.message}</Typography>
                    <LinearProgress variant={sending && sending.total ? 'determinate' : 'indeterminate'} value={sending && sending.total ? Math.min(100, (sending.loaded / sending.total) * 100) : 0} sx={{ height: 8, borderRadius: 4 }} />
                    {sending && sending.total > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {formatSize(sending.loaded)} dari {formatSize(sending.total)}. Jangan tutup halaman ini sampai selesai.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={Boolean(done)} onClose={() => setDone(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircleIcon sx={{ color: '#4ade80' }} /> Terima kasih!</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Kiriman <b>{done && done.title}</b> sudah masuk antrean tinjauan admin. Statusnya bisa dilihat di bagian "Kiriman kamu" di halaman ini.
                    </Typography>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,122,26,.1)', border: '1px solid rgba(255,122,26,.35)', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Kode kiriman</Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: 30, letterSpacing: 4, color: ACCENT }}>{done && done.code}</Typography>
                    </Box>
                    <Button fullWidth startIcon={<ContentCopyIcon />} sx={{ mt: 1.5 }} onClick={async () => {
                        try {
                            await navigator.clipboard.writeText(statusLink);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                        } catch { /* clipboard diblokir */ }
                    }}>
                        {copied ? 'Link status disalin' : 'Salin link status (untuk dibuka di perangkat lain)'}
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDone(null)} variant="contained">Tutup</Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}
